// src/components/ResetPasswordModal.jsx
import { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { X, KeyRound, Eye, EyeOff, ShieldCheck } from "lucide-react";

const API =
  (import.meta?.env?.VITE_API_BASE_URL ?? "").replace(
    /\/+$/,
    ""
  );

export default function ResetPasswordModal({
  open,
  tenantId,
  username,
  onClose,
  onSuccess,
}) {
  const [pwd1, setPwd1] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [saving, setSaving] = useState(false);

  // reveal toggles (pure UI)
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);

  useEffect(() => {
    if (open) {
      setPwd1("");
      setPwd2("");
      setSaving(false);
      setShow1(false);
      setShow2(false);
    }
  }, [open]);

  if (!open) return null;

  const validate = () => {
    if (!pwd1 || !pwd2) return "Password fields cannot be empty.";
    if (pwd1 !== pwd2) return "Passwords do not match.";
    if (pwd1.length < 8) return "Password must be at least 8 characters.";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) {
      Swal.fire("Invalid password", err, "warning");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `https://superadmin.nissiindia.com/api/mysql/tenant/${Number(tenantId)}/admins/${encodeURIComponent(
          String(username || "").trim()
        )}/reset_password`,
        { newPassword: pwd1 }
      );
      Swal.fire("Success", "Password reset successfully.", "success");
      onSuccess?.();
    } catch (e) {
      console.error(e);
      Swal.fire("Error", e?.response?.data?.error || e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const titleId = "reset-password-title";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      {/* Card */}
      <div
        className="w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200/70 bg-white/90 shadow-2xl backdrop-blur"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header (glass/pill style) */}
        <div className="flex items-center justify-between gap-3 border-b border-slate-200/70 bg-gradient-to-r from-indigo-50 to-blue-50 px-5 py-3">
          <div className="flex items-center gap-2">
            <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 text-white shadow">
              <KeyRound className="h-4 w-4" />
            </div>
            <h3 id={titleId} className="text-sm font-semibold text-slate-800">
              Reset Password — <span className="font-normal">{username}</span>
            </h3>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-200/60 hover:text-slate-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5">
          {/* New Password */}
          <div className="mb-4">
            <label className="mb-1 block text-sm font-medium text-slate-700">
              New Password
            </label>
            <div className="relative">
              <KeyRound className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type={show1 ? "text" : "password"}
                value={pwd1}
                onChange={(e) => setPwd1(e.target.value)}
                placeholder="At least 8 characters"
                className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-10 py-2 text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                onKeyDown={(e) => e.key === "Enter" && submit()}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShow1((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-500 hover:bg-slate-100"
                title={show1 ? "Hide" : "Show"}
              >
                {show1 ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Min 8 chars. Add uppercase, lowercase, numbers, or symbols for
              better strength.
            </p>
          </div>

          {/* Confirm Password */}
          <div className="mb-2">
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Confirm Password
            </label>
            <div className="relative">
              <KeyRound className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type={show2 ? "text" : "password"}
                value={pwd2}
                onChange={(e) => setPwd2(e.target.value)}
                placeholder="Re-enter new password"
                className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-10 py-2 text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                onKeyDown={(e) => e.key === "Enter" && submit()}
              />
              <button
                type="button"
                onClick={() => setShow2((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-500 hover:bg-slate-100"
                title={show2 ? "Hide" : "Show"}
              >
                {show2 ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          {/* Hint */}
          <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600 ring-1 ring-slate-200">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            Your password will be securely hashed on the server.
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-slate-200/70 bg-white/70 px-5 py-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="inline-flex items-center rounded-lg bg-white px-4 py-2 text-slate-900 ring-1 ring-slate-300 shadow hover:bg-slate-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="inline-flex items-center rounded-lg bg-slate-900 px-4 py-2 text-white shadow hover:bg-black focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-60"
          >
            {saving ? "Saving..." : "Reset Password"}
          </button>
        </div>
      </div>
    </div>
  );
}
